import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { CatalogImportMode } from './catalogImportMode';
import { isDirty, isImporting } from './catalogImportSelectors';
import { useCatalogImport } from './CatalogImportProvider';
import { useLeaveConfirmation } from './useLeaveConfirmation';

type CatalogImportLeaveGuardProps = {
    /** The flow's own path prefix; navigations inside it are never blocked. */
    scopePath: string;
    /** What the dialog asks the user to leave: the wizard or this import. */
    mode: CatalogImportMode;
};

/**
 * Mounted inside a `CatalogImportProvider`: while a parsed file has not been
 * imported, leaving the flow asks first. Leave resets the store and goes;
 * Stay keeps everything. The dialog has the shape of the app's confirm
 * dialog (`DeleteConfirmDialog`) with neutral copy, since nothing has been
 * written. While an import is on its way nothing navigates at all (signing
 * out aside), and the dialog does not open.
 */
export const CatalogImportLeaveGuard: React.FC<
    CatalogImportLeaveGuardProps
> = ({ scopePath, mode }) => {
    const { t } = useTranslation('pages');
    const { state, actions } = useCatalogImport();
    const leave = useLeaveConfirmation(
        isDirty(state),
        scopePath,
        actions.reset,
        isImporting(state),
    );

    return (
        <Modal
            isOpen={leave.isOpen}
            onOpenChange={(open) => !open && leave.onStay()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('catalog-import-leave-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {mode === 'shell'
                                    ? t('catalogImport.leave.titleShell')
                                    : t('catalogImport.leave.title')}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body>
                            <p className="text-sm text-muted">
                                {t('catalogImport.leave.body')}
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={leave.onStay}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'catalog-import-leave-stay',
                                )}
                            >
                                {t('catalogImport.leave.stay')}
                            </Button>
                            <Button
                                onPress={leave.onLeave}
                                variant="danger"
                                {...getNodeTestIdProp(
                                    'catalog-import-leave-confirm',
                                )}
                            >
                                {t('catalogImport.leave.leave')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
